/* global gdAlertify */
import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate } from "../utils/utils";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Helper to decode JWT locally
const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

const formatFechaCL = (fechaStr) => {
    if (!fechaStr) return "";
    const [y, m, d] = fechaStr.split("-");
    return `${d}-${m}-${y}`;
};

const ModalNuevaNovedad = ({ isOpen, onClose, onSuccess, novedadEditar }) => {
    const quillRef = useRef(null);
    const [observacion, setObservacion] = useState("");
    const [fechaDesde, setFechaDesde] = useState(new Date().toISOString().split("T")[0]);
    const [fechaHasta, setFechaHasta] = useState(new Date().toISOString().split("T")[0]);

    useEffect(() => {
        if (isOpen) {
            if (novedadEditar) {
                setObservacion(novedadEditar.observacion || "");
                setFechaDesde(novedadEditar.fecha_desde ? novedadEditar.fecha_desde.split("T")[0] : new Date().toISOString().split("T")[0]);
                setFechaHasta(novedadEditar.fecha_hasta ? novedadEditar.fecha_hasta.split("T")[0] : new Date().toISOString().split("T")[0]);
            } else {
                const hoy = new Date().toISOString().split("T")[0];
                setFechaDesde(hoy);
                setFechaHasta(hoy);
                
                const formatFechaCL = (fechaStr) => {
                    const [y, m, d] = fechaStr.split('-');
                    return `${d}-${m}-${y}`;
                };

                // Inicializar observación con fechas en negrita y plantilla
                setObservacion(`
                    <p><strong>Fecha Desde: ${formatFechaCL(hoy)} - Fecha Hasta: ${formatFechaCL(hoy)}</strong></p>
                    <p><br></p>
                    <p><strong>INGRESOS:</strong></p><p><br></p>
                    <p><strong>CIERRES:</strong></p><p><br></p>
                    <p><strong>PERSONAL DISPONIBLE EN TURNO:</strong></p><p><br></p>
                    <p><strong>PERSONAL DISPONIBLE SIN TURNO:</strong></p><p><br></p>
                    <p><strong>SOLICITAN CAMBIO DE PACIENTES:</strong></p><p><br></p>
                    <p><strong>SISTEMA SK:</strong></p><p><br></p>
                    <p><strong>RADIOTAXIS:</strong></p><p><br></p>
                `);
            }
        }
    }, [isOpen, novedadEditar]);

    const actualizarFechasEnEditor = (nuevaFechaDesde, nuevaFechaHasta) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const encabezado = `Fecha Desde: ${formatFechaCL(nuevaFechaDesde)} - Fecha Hasta: ${formatFechaCL(nuevaFechaHasta)}`;
        const primeraLinea = editor.getText(0, editor.getText().indexOf("\n") + 1).trim();
        const esEncabezadoFecha = primeraLinea.startsWith("Fecha Desde:");

        if (esEncabezadoFecha) {
            editor.deleteText(0, primeraLinea.length, "user");
        } else {
            editor.insertText(0, "\n", "user");
        }

        editor.insertText(0, encabezado, { bold: true }, "user");
    };

    // Actualizar solo la primera línea del editor, sin reconstruir su contenido.
    const handleFechaDesdeChange = (e) => {
        const nuevaFechaDesde = e.target.value;
        setFechaDesde(nuevaFechaDesde);

        if (!novedadEditar) {
            actualizarFechasEnEditor(nuevaFechaDesde, fechaHasta);
        }
    };

    const handleFechaHastaChange = (e) => {
        const nuevaFechaHasta = e.target.value;
        setFechaHasta(nuevaFechaHasta);

        if (!novedadEditar) {
            actualizarFechasEnEditor(fechaDesde, nuevaFechaHasta);
        }
    };

    const handleGuardar = async () => {
        // Validation: Check if editor is empty or just contains HTML tags
        const strippedContent = observacion.replace(/<[^>]*>/g, '').trim();

        if (!strippedContent) {
            gdAlertify.notify("Debes ingresar una observación.", "error");
            return;
        }

        try {
            gdAlertify.showLoader();
            const payload = {
                fecha: fechaDesde, // Enviar fecha_desde como fecha al servidor
                fecha_desde: fechaDesde,
                fecha_hasta: fechaHasta,
                observacion,
            };

            if (novedadEditar) {
                payload.id = novedadEditar.id;
                await axiosInstance.post("/novedades/guardar", {
                    ...payload,
                    id: novedadEditar ? novedadEditar.id : null
                });
            } else {
                await axiosInstance.post("/novedades/guardar", payload);
            }

            gdAlertify.hideLoader();
            gdAlertify.notify(novedadEditar ? "Novedad actualizada." : "Novedad guardada.", "success");
            onSuccess();
            onClose();
        } catch (error) {
            gdAlertify.hideLoader();
            console.error("Error al guardar novedad: ", error);
            gdAlertify.notify("Error al guardar la novedad.", "error");
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            // [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
            [{ 'align': [] }],
            // ['clean']                                         // remove formatting button (Tx)
            // ['link', 'image', 'video']                        // link and image, video (Adjuntar)
        ]
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: "block", zIndex: 1065 }} tabIndex="-1">
                <div className="modal-dialog modal-xl"> {/* Changed to modal-xl for larger size */}
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{novedadEditar ? "Editar Novedad" : "Nueva Novedad"}</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Fecha Desde</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fechaDesde}
                                        onChange={handleFechaDesdeChange}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Fecha Hasta</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fechaHasta}
                                        onChange={handleFechaHastaChange}
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                {/* Removed label "Observación" based on "quites eladjunto el tx" interpretation */}
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={observacion}
                                    onChange={setObservacion}
                                    modules={modules}
                                    style={{ height: '300px', marginBottom: '50px' }} // Increased height
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button className="btn btn-primary" onClick={handleGuardar}>
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
        </>
    );
};

const Novedades = ({ modal = false, onClose }) => {
    const [data, setData] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [novedadEditar, setNovedadEditar] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            const decoded = parseJwt(token);
            if (decoded) {
                // Check multiple possible ID fields
                setCurrentUser(decoded.id || decoded.data?.id || decoded.user_id || decoded.sub);
            }
        }
        fetchNovedades();
    }, []);

    const fetchNovedades = async () => {
        setLoading(true);
        try {
            // Se eliminan los filtros de fecha para traer todas las novedades
            const response = await axiosInstance.get("/novedades/lista");
            if (response.data && response.data.data) {
                setData(response.data.data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error al buscar novedades: ", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEliminar = (id) => {
        gdAlertify.confirm({
            title: "Eliminar Novedad",
            message: "¿Estás seguro de que deseas eliminar esta novedad?",
            onConfirm: async () => {
                try {
                    gdAlertify.showLoader();
                    await axiosInstance.post("/novedades/eliminar", { id });
                    gdAlertify.hideLoader();
                    gdAlertify.notify("Novedad eliminada.", "success");
                    fetchNovedades();
                } catch (error) {
                    gdAlertify.hideLoader();
                    console.error("Error al eliminar novedad: ", error);
                    gdAlertify.notify("Error al eliminar.", "error");
                }
            },
            onCancel: () => { },
        });
    };

    const openModalEditar = (novedad) => {
        setNovedadEditar(novedad);
        setModalOpen(true);
    };

    const openModalNuevo = () => {
        setNovedadEditar(null);
        setModalOpen(true);
    };

    const columns = [
        { name: "id", title: "#", width: 50 },
        {
            name: "usuario",
            title: "Creado por",
            render: (row) => row.usuario || row.nombre_usuario || "N/A",
            width: 150
        },
        {
            name: "observacion",
            title: "Observación",
            render: (row) => {
                const fechaDesdeStr = row.fecha_desde ? new Date(row.fecha_desde).toLocaleDateString('es-CL') : '';
                const fechaHastaStr = row.fecha_hasta ? new Date(row.fecha_hasta).toLocaleDateString('es-CL') : '';
                const fechasHTML = (fechaDesdeStr && fechaHastaStr)
                    ? `<p><strong>Fecha Desde: ${fechaDesdeStr} - Fecha Hasta: ${fechaHastaStr}</strong></p>`
                    : '';

                return (
                    <div dangerouslySetInnerHTML={{ __html: fechasHTML + (row.observacion || '') }} />
                );
            }
        },
        {
            name: "acciones",
            title: "Acciones",
            render: (row) => {
                const isOwner = currentUser && (parseInt(currentUser) === parseInt(row.usuario_id));
                return (
                    <div className="d-flex gap-2">
                        {isOwner && (
                            <>
                                <button
                                    className="btn btn-warning btn-sm text-white"
                                    onClick={() => openModalEditar(row)}
                                    title="Editar"
                                >
                                    <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleEliminar(row.id)}
                                    title="Eliminar"
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </>
                        )}
                    </div>
                );
            },
            width: 120,
        },
    ];

    const contenido = (
        <>
                <div className={`${modal ? "modal-header" : "d-flex justify-content-between align-items-center mb-4"}`}>
                    <h3>Novedades Coordinación</h3>
                    <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-primary" onClick={openModalNuevo}>
                            Nueva Novedad
                        </button>
                        {modal && <button type="button" className="btn-close" onClick={onClose}></button>}
                    </div>
                </div>

                <div className={modal ? "modal-body overflow-auto" : ""}>
                    <GdTable
                        columns={columns}
                        data={data}
                        defaultItemsPerPage={25}
                        itemsPerPageOptions={[10, 25, 50, 100]}
                        initialSort={[0, "desc"]}
                        loading={loading}
                        exportFilename="novedades.xlsx"
                    />
                </div>
        </>
    );

    const editorNovedad = (
            <ModalNuevaNovedad
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={fetchNovedades}
                novedadEditar={novedadEditar}
            />
    );

    if (modal) {
        return (
            <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: "90vw", width: "90vw", maxHeight: "90vh", margin: "5vh auto" }}>
                    <div className="modal-content" style={{ maxHeight: "90vh" }}>
                        {contenido}
                    </div>
                </div>
                {editorNovedad}
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="card p-4">
                {contenido}
            </div>
            {editorNovedad}
        </div>
    );
};

export default Novedades;
